import { useEffect, useState, type ReactNode } from "react";
import { Check, Eye, EyeOff, Settings2 } from "lucide-react";
import { ProviderIcon } from "./ProviderIcon";
import type { ProviderConnectionState } from "./hooks/useProviderConnections";
import type { ProviderSetupDefinition } from "./hooks/useProviderConnections";
import {
  HARPER_PROVIDER_VISIBILITY_CHANGED_EVENT,
  INTERACTIVE_CHAT_PROVIDERS,
  readHiddenChatProviders,
  toggleChatProviderVisibility,
  type HarperProvider,
} from "./provider-options";

interface ProviderPickerListProps {
  provider: HarperProvider;
  connections: Map<HarperProvider, ProviderConnectionState>;
  loading: boolean;
  error: string | null;
  definitions: ProviderSetupDefinition[];
  onSelect: (provider: HarperProvider) => void;
  onOpenSettings: (provider: HarperProvider) => void;
  onVisibilityChange: (provider: HarperProvider, hidden: boolean) => void;
  onModelChange: (provider: HarperProvider, model: string) => Promise<void>;
}

export function ProviderPickerList(props: ProviderPickerListProps) {
  const [hiddenProviders, setHiddenProviders] = useState<HarperProvider[]>(
    readHiddenChatProviders,
  );
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    const refresh = () => setHiddenProviders(readHiddenChatProviders());
    window.addEventListener(HARPER_PROVIDER_VISIBILITY_CHANGED_EVENT, refresh);
    return () =>
      window.removeEventListener(
        HARPER_PROVIDER_VISIBILITY_CHANGED_EVENT,
        refresh,
      );
  }, []);

  const hidden = new Set(hiddenProviders);
  const visibleProviders = INTERACTIVE_CHAT_PROVIDERS.filter(
    (item) => !hidden.has(item.id),
  );
  const concealedProviders = INTERACTIVE_CHAT_PROVIDERS.filter((item) =>
    hidden.has(item.id),
  );

  const changeVisibility = (provider: HarperProvider) => {
    const next = toggleChatProviderVisibility(provider);
    const isHidden = next.includes(provider);
    setHiddenProviders(next);
    props.onVisibilityChange(provider, isHidden);
  };

  return (
    <div className="min-h-0 overflow-y-auto p-1" aria-label="Provider options">
      {visibleProviders.map((item) => (
        <ProviderRow
          key={item.id}
          item={item}
          active={item.id === props.provider}
          connection={props.connections.get(item.id)}
          definition={props.definitions.find((entry) => entry.id === item.id)}
          onSelect={props.onSelect}
          onOpenSettings={props.onOpenSettings}
          onVisibilityChange={changeVisibility}
          onModelChange={props.onModelChange}
        />
      ))}
      {concealedProviders.length > 0 ? (
        <div className="mt-1 border-t border-[color-mix(in_srgb,var(--fintheon-accent)_10%,transparent)] pt-1">
          <button
            type="button"
            role="menuitem"
            onClick={() => setShowHidden((current) => !current)}
            className="flex w-full items-center gap-2 px-2.5 py-2 text-left text-[10px] text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
          >
            <Eye size={12} />
            {showHidden
              ? "Hide concealed providers"
              : `Show concealed providers (${concealedProviders.length})`}
          </button>
          {showHidden
            ? concealedProviders.map((item) => (
                <ProviderRow
                  key={item.id}
                  item={item}
                  active={false}
                  concealed
                  connection={props.connections.get(item.id)}
                  definition={props.definitions.find(
                    (entry) => entry.id === item.id,
                  )}
                  onSelect={props.onSelect}
                  onOpenSettings={props.onOpenSettings}
                  onVisibilityChange={changeVisibility}
                  onModelChange={props.onModelChange}
                />
              ))
            : null}
        </div>
      ) : null}
      {props.loading ? (
        <p className="px-2.5 py-2 text-[10px] text-[var(--fintheon-muted)]">
          Checking connections...
        </p>
      ) : null}
      {props.error ? (
        <p className="px-2.5 py-2 text-[10px] text-red-300/80">{props.error}</p>
      ) : null}
    </div>
  );
}

function ProviderRow(props: {
  item: (typeof INTERACTIVE_CHAT_PROVIDERS)[number];
  active: boolean;
  concealed?: boolean;
  connection: ProviderConnectionState | undefined;
  definition: ProviderSetupDefinition | undefined;
  onSelect: (provider: HarperProvider) => void;
  onOpenSettings: (provider: HarperProvider) => void;
  onVisibilityChange: (provider: HarperProvider) => void;
  onModelChange: (provider: HarperProvider, model: string) => Promise<void>;
}) {
  const ready = props.connection?.status === "ready";
  const models = props.connection?.models.length
    ? props.connection.models
    : (props.definition?.models ?? []);
  const model =
    props.connection?.model ??
    props.definition?.defaultModel ??
    props.item.modelId;
  const detail = ready
    ? props.connection?.accountLabel || model
    : providerDetail(props.connection);

  return (
    <div
      className={`group ${props.concealed ? "opacity-55" : ""}`}
      style={{
        background: props.active
          ? "color-mix(in srgb, var(--fintheon-accent) 8%, transparent)"
          : undefined,
      }}
    >
      <div className="flex min-w-0 items-center gap-1 px-1.5 py-1">
        <button
          type="button"
          role="menuitem"
          disabled={props.concealed}
          onClick={() =>
            ready
              ? props.onSelect(props.item.id)
              : props.onOpenSettings(props.item.id)
          }
          className="flex min-w-0 flex-1 items-center gap-2.5 px-1 py-1.5 text-left transition-colors hover:text-[var(--fintheon-text)] disabled:cursor-default"
        >
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[var(--fintheon-accent)]">
            <ProviderIcon icon={props.item.icon} className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs text-[var(--fintheon-text)]">
              {props.item.label}
            </span>
            <span className="mt-0.5 block truncate text-[10px] text-[var(--fintheon-muted)]">
              {ready ? model : detail}
            </span>
          </span>
          {props.active ? (
            <Check size={12} color="var(--fintheon-accent)" />
          ) : null}
        </button>
        <span
          aria-label={ready ? "Connected" : "Setup required"}
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{
            background: ready
              ? "rgba(240,234,214,0.66)"
              : "color-mix(in srgb, var(--fintheon-accent) 62%, transparent)",
          }}
        />
        <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <IconButton
            label={`Configure ${props.item.label}`}
            onClick={() => props.onOpenSettings(props.item.id)}
          >
            <Settings2 size={13} />
          </IconButton>
          <IconButton
            label={
              props.concealed
                ? `Show ${props.item.label}`
                : `Hide ${props.item.label}`
            }
            onClick={() => props.onVisibilityChange(props.item.id)}
          >
            {props.concealed ? <Eye size={13} /> : <EyeOff size={13} />}
          </IconButton>
        </div>
      </div>
      {props.active && ready && models.length > 0 ? (
        <label className="flex items-center gap-2 px-9 pb-2 text-[9px] uppercase tracking-[0.1em] text-[var(--fintheon-muted)]">
          Model
          <select
            value={model}
            onChange={(event) =>
              void props.onModelChange(props.item.id, event.target.value)
            }
            className="min-w-0 flex-1 border-0 bg-transparent text-[10px] normal-case tracking-normal text-[var(--fintheon-text)] outline-none"
          >
            {models.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

function IconButton(props: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      aria-label={props.label}
      title={props.label}
      onClick={props.onClick}
      className="inline-flex h-6 w-6 items-center justify-center bg-transparent text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
    >
      {props.children}
    </button>
  );
}

function providerDetail(
  connection: ProviderConnectionState | undefined,
): string {
  if (connection?.status === "pending") return "Verifying";
  if (
    connection?.status === "expired" ||
    connection?.status === "disconnected"
  ) {
    return "Reconnect required";
  }
  if (connection?.status === "invalid") return "Request failed";
  return "Set up to use";
}
